import { useState } from 'react'

import { Text } from '@/components/ui/Text'
import { ProfileCard } from '@/lib/api/client'
import { normalizeDashes } from '@/lib/utils/normalize-dashes'

const CLAMP_CLASSES: Record<number, string> = {
  2: 'line-clamp-2',
  3: 'line-clamp-3',
  4: 'line-clamp-4',
  5: 'line-clamp-5',
  6: 'line-clamp-6',
  8: 'line-clamp-[8]',
}

interface TwinBioProps {
  profileCard?: ProfileCard | null
  fallbackBio?: string | null
  /** Tailwind text color class for the intro text. Defaults to current text color. */
  introClassName?: string
  /** Tailwind text color class for highlights. Defaults to muted text. */
  highlightClassName?: string
  /** Maximum lines to clamp the fallback bio to (old format). 0 = no clamp. */
  fallbackClamp?: number
  /** Tailwind class for the expand/collapse toggle link. */
  toggleClassName?: string
  /** Show expand/collapse toggle for the fallback (old-format) bio too. */
  collapsible?: boolean
}

/**
 * Renders a twin's biographical profile in the new structured format
 * (short intro + bullet highlights) when available, falling back to
 * the legacy plain-text bio paragraph for older profile cards.
 *
 * When highlights are present and a fallbackBio (full narrative) exists,
 * a "Show more" toggle reveals the full summary below the highlights.
 */
export function TwinBio({
  profileCard,
  fallbackBio,
  introClassName = 'text-current',
  highlightClassName = 'text-muted-foreground',
  fallbackClamp = 0,
  toggleClassName = 'text-primary',
  collapsible = false,
}: TwinBioProps) {
  const [expanded, setExpanded] = useState(false)

  const highlights = profileCard?.highlights
  // Display seam: stored bios and highlights may carry en or em dashes (ranges); never shown.
  const rawBio = profileCard?.bio ?? fallbackBio ?? null
  const bio = rawBio ? normalizeDashes(rawBio) : rawBio

  if (!bio && (!highlights || highlights.length === 0)) {
    return null
  }

  const hasHighlights = highlights && highlights.length > 0

  if (hasHighlights) {
    return (
      <div className="flex flex-col gap-2" data-testid="twin-bio">
        {bio && (
          <Text size="sm" className={`leading-[1.5] ${highlightClassName}`}>
            {bio}
          </Text>
        )}
        <ul className="flex flex-col gap-1 pl-0" data-testid="twin-bio-highlights">
          {highlights.map((highlight, idx) => (
            <li
              key={idx}
              className={`flex items-start gap-2 text-sm leading-[1.5] ${highlightClassName}`}
            >
              <span className="mt-[7px] block h-1 w-1 shrink-0 rounded-full bg-current opacity-50" />
              <span>{normalizeDashes(highlight)}</span>
            </li>
          ))}
        </ul>
      </div>
    )
  }

  const shouldClamp = collapsible ? !expanded : fallbackClamp > 0
  const clampClass = shouldClamp ? (CLAMP_CLASSES[fallbackClamp] ?? '') : ''

  return (
    <div className="flex flex-col gap-1" data-testid="twin-bio">
      <Text
        size="sm"
        className={`leading-[1.5] ${highlightClassName} ${clampClass}`}
      >
        {bio}
      </Text>
      {collapsible && (
        <button
          type="button"
          data-testid="twin-bio-expand-toggle"
          className={`self-start text-sm font-medium transition-opacity hover:opacity-80 ${toggleClassName}`}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  )
}
